import * as React from 'react';
import styled, { CSSObject } from 'styled-components';

import * as Theme from '../../serviceTheme';
import { useIORef } from '../../core/hooks';
import { pos7, size3, size1 } from '../../core/Trace';
import { useFocusContext } from '../../core/Focus';

import { fadeBottom } from '../Animation';
import { Context } from '../Helpers';
import { splitClasses } from '../utils';
import { ctxSelectClass } from './shared';
import { 
    useSetter, useGetter,
    useEffect, slot,
} from './slots';

type WidthMod = 'select-width' | 'max-content';

interface IWidthMod {
    widthMod: WidthMod,
}

interface Height {
    maxHeight: number | 'auto',
}


const getSize = (type: WidthMod) => {
    switch(type){
        case 'max-content':
            return size1;
        case 'select-width':
            return size3;
    }
}

const Component = slot('dropdown')(({ width = 'select-width', maxHeight = 'auto', className, children }) => {

    const {
        read: isOpen,
        write: updateOpen,
    } = useIORef(false);

    const { focus, blur } = useFocusContext();


    const _open = updateOpen(true);
    const _close = updateOpen(false);
    
    const selectController =    useGetter('select');
    const service =             useGetter('service');
    const [selectInactivate] =  useEffect('selectInactivate');

    const [, runOpen] =         useEffect('dropdownOpened');
    const [, runClose] =        useEffect('dropdownClosed');

    const open = runOpen(() => {
        _open();
    });

    const close = runClose(() => {
        _close();
    });

    useSetter('dropdown', {
        isOpen,
        open,
        close,
    });

    const size = getSize(width);

    const onInHandler = () => {
        if(!selectController.isActive()){
            return;
        }

        open();
    }

    const onOutHandler = () => {
        if(!selectController.isActive()){
            return;
        }

        close();
    }


    React.useEffect(() => {
        close();
    }, [selectInactivate]);


    return (
        <Context
            innerRef={service.controlRef()}
            animation={fadeBottom}
            isShow={isOpen()}
            position={pos7}
            size={size}
            onIn={onInHandler}
            onOut={onOutHandler}
            id={selectController.id}>
            <Dropdown
                tabIndex={0}
                onFocus={focus(open)}
                onBlur={blur(close)}
                className={splitClasses(ctxSelectClass('dropdown'), className)}
                widthMod={width}
                maxHeight={maxHeight}>
                {children}
            </Dropdown>
        </Context>
    );
});

export default Component;

const getWidth = ({ widthMod }: IWidthMod): CSSObject => {
    switch(widthMod){
        case 'select-width':
            return { width: 'auto' }
        case 'max-content':
            return { width: 'max-content' }
    }
}

const maxHeight = ({ maxHeight }: Height) => ({ maxHeight });

interface DropdownStyledProps extends IWidthMod, Height {}

const Dropdown = styled.div<DropdownStyledProps>`
    position: relative;
    transition: all .33s ease-in-out;
    border-radius: 3px;
    box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.16);
    background-color: #fff;
    color: initial;
    z-index: 500;
    overflow: auto;
    top: 5px;
    ${getWidth}
    ${maxHeight};

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        padding: 1px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${Theme.safeGetPrimary};
        border-radius: 40px 0px 0px 40px;
    }
`;